/*
  Product finder — home page launcher.

  Two jobs, both optional: open/close the facet trays, and report how many products the
  current selection would land on. The REDIRECT is not one of them — the section is a plain
  GET form whose inputs already carry Shopify's own filter params, so the browser serializes
  and navigates on its own. Nothing here is required for the section to work.

  The count is read from the collection page itself rather than computed, so it can never
  disagree with what the visitor lands on. Verified against the preview theme:
    /collections/all                                    -> 441
    /collections/all?filter.p.m.my_fields.gender=Female -> 4

  SECTION ID DISCOVERY. Fetching the whole collection page costs ~1.2MB; fetching one section
  through the Section Rendering API costs ~3.6KB. But the id is `template--<TEMPLATE id>__…`
  and template ids differ per template — the home page is template--22713681412345, the
  collection template--22713681379577 — so it cannot be derived from this page, in Liquid or
  from the DOM. It is therefore discovered once from a full fetch, cached in sessionStorage,
  and every later request is the cheap one. The discovery fetch is not wasted: it carries the
  first count too.
*/
(function () {
  'use strict';

  var COUNT_SELECTOR = '.plp-hero__count-value';
  var SECTION_RE = /shopify-section-(template--\d+__section)\b/;
  var CACHE_KEY = 'km-find-section-id';
  var DEBOUNCE_MS = 350;

  /* sessionStorage throws in some privacy modes — never let that break the form. */
  function cacheGet() {
    try {
      return window.sessionStorage.getItem(CACHE_KEY);
    } catch (e) {
      return null;
    }
  }

  function cacheSet(value) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, value);
    } catch (e) {
      /* no-op */
    }
  }

  function initLauncher(form) {
    /* The script tag ships once per section, so a second copy must not rebind a form. */
    if (form.dataset.kmBound) return;
    form.dataset.kmBound = 'true';

    var countEl = form.querySelector('[data-km-find-count]');
    var submitEl = form.querySelector('.km-find__submit');
    var toggles = Array.prototype.slice.call(form.querySelectorAll('[data-km-find-toggle]'));
    var baseUrl = form.getAttribute('data-km-find-url') || form.getAttribute('action');
    var idleText = countEl ? countEl.textContent : '';
    var timer = null;
    var controller = null;

    /* Result strings come from the locale file through data-* on the form. */
    var strings = form.dataset;
    function plural(one, other, count, fallbackOne, fallbackOther) {
      if (count === 1) return one || fallbackOne;
      return (other || fallbackOther).replace('{{ count }}', String(count));
    }

    /* ---------- trays ---------- */
    /* Exclusive open, matching the collection page's bar: two open trays stack and push the
       button the launcher exists to feed off the screen. */
    function closeAll(except) {
      toggles.forEach(function (toggle) {
        if (toggle === except) return;
        var panel = document.getElementById(toggle.getAttribute('aria-controls'));
        toggle.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var panel = document.getElementById(toggle.getAttribute('aria-controls'));
        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        closeAll(toggle);
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (panel) panel.hidden = isOpen;
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll(null);
    });

    document.addEventListener('click', function (event) {
      if (!form.contains(event.target)) closeAll(null);
    });

    /* ---------- the query ---------- */
    /* Same serialization the theme's own facets.js performs (assets/facets.js:54-65),
       including dropping empty price bounds so the landing URL stays clean. */
    function buildParams() {
      var params = new URLSearchParams(new FormData(form));
      if (params.get('filter.v.price.gte') === '') params.delete('filter.v.price.gte');
      if (params.get('filter.v.price.lte') === '') params.delete('filter.v.price.lte');
      return params;
    }

    function activeCount() {
      var params = buildParams();
      var total = 0;
      params.forEach(function () {
        total += 1;
      });
      return total;
    }

    function setCount(text, disable) {
      if (countEl) countEl.textContent = text;
      if (!submitEl) return;
      submitEl.disabled = !!disable;
      submitEl.setAttribute('aria-disabled', disable ? 'true' : 'false');
    }

    /* Falls back to the honest "N filters set" whenever the real number is unavailable —
       a wrong count is worse than no count, because the visitor lands on it. */
    function setFallback() {
      var total = activeCount();
      if (!total) {
        setCount(idleText, false);
        return;
      }
      setCount(
        plural(strings.kmFindSetOne, strings.kmFindSetOther, total, '1 filter set', '{{ count }} filters set'),
        false
      );
    }

    function readCount(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(COUNT_SELECTOR);
      if (!el) return null;
      var parsed = parseInt(el.textContent.replace(/[^0-9]/g, ''), 10);
      return isNaN(parsed) ? null : parsed;
    }

    function render(count) {
      if (count === null) {
        setFallback();
        return;
      }
      if (count === 0) {
        setCount(strings.kmFindNone || 'No products match — loosen a filter', true);
        return;
      }
      setCount(
        plural(strings.kmFindShowOne, strings.kmFindShowOther, count, 'Show 1 product', 'Show {{ count }} products'),
        false
      );
    }

    function refresh() {
      if (!countEl) return;

      if (controller) controller.abort();
      controller = new AbortController();

      var params = buildParams();
      var query = params.toString();
      var cached = cacheGet();
      var url = baseUrl + (query ? '?' + query : '');
      if (cached) url += (query ? '&' : '?') + 'section_id=' + encodeURIComponent(cached);

      fetch(url, { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('bad response');
          return response.text();
        })
        .then(function (html) {
          /* Discovery only happens on the full document, i.e. the first request. */
          if (!cached) {
            var match = html.match(SECTION_RE);
            if (match) cacheSet(match[1]);
          }
          render(readCount(html));
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          setFallback();
        });
    }

    function schedule() {
      window.clearTimeout(timer);
      timer = window.setTimeout(refresh, DEBOUNCE_MS);
    }

    form.addEventListener('change', schedule);
    form.addEventListener('input', function (event) {
      if (event.target && event.target.type === 'number') schedule();
    });

    /* A disabled submit must never survive into a fresh selection. */
    form.addEventListener('submit', function () {
      window.clearTimeout(timer);
      if (controller) controller.abort();
    });
  }

  function init() {
    var forms = document.querySelectorAll('[data-km-find-form]');
    Array.prototype.forEach.call(forms, initLauncher);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    var form = event.target.querySelector('[data-km-find-form]');
    if (form) initLauncher(form);
  });
})();
